import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Car, Flag, Gauge, MapPin, ShieldCheck } from 'lucide-react';
import api from '@/lib/axios';
import { resolveImageUrl } from '@/lib/listing';
import { useSeo, useJsonLd } from '../hooks/useSeo';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';
import PromoBadges from '@/components/marketplace/PromoBadges';
import ListingContactForm from '@/components/marketplace/ListingContactForm';
import ConversationStartForm from '@/components/marketplace/ConversationStartForm';
import { AdSlot } from '@/components/marketplace/ads/AdSlot';
import { useConversationsEnabled } from '@/lib/conversations';
import ReportListingModal from '@/components/marketplace/ReportListingModal';
import { SaleCard, money, km } from './MarketplaceBuyPage';

const FUEL = { gasolina: 'Gasolina', gasoleo: 'Gasóleo', gpl: 'GPL', eletrico: 'Elétrico', hibrido: 'Híbrido', hibrido_plugin: 'Híbrido plug-in' };
const ESTADO = { operacional: 'Operacional', operacional_anomalias: 'Operacional com anomalias', avariada: 'Avariada', imobilizada: 'Imobilizada', pecas: 'Para peças' };
const VENDEDOR = { particular: 'Particular', empresa: 'Empresa', profissional: 'Profissional', stand: 'Stand' };
const TRI = { sim: 'Sim', nao: 'Não', desconhecido: 'Desconhecido' };

const Row = ({ label, value, testId }) => (
  value === null || value === undefined || value === '' ? null : (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-semibold text-slate-800" data-testid={testId}>{value}</span>
    </div>
  )
);

export default function MarketplaceSaleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState('');
  const [photo, setPhoto] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const conversasAtivas = useConversationsEnabled();

  useEffect(() => {
    api.get(`/marketplace/venda/publicacoes/${id}`)
      .then((r) => setItem(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Anúncio não encontrado'));
  }, [id]);

  useSeo({
    title: item ? `${item.titulo} · ${money(item.preco)} | Marketplace TVDE` : 'Viatura à venda | Marketplace TVDE',
    description: item
      ? `${item.categoria_label} · ${item.ano} · ${km(item.quilometros)} · ${FUEL[item.combustivel] || ''} · ${item.localizacao}. Anúncio publicado por ${item.nome_publico}.`
      : 'Viatura TVDE à venda em Portugal.',
    path: `/comprar-viaturas-tvde/${id}`,
    image: item?.foto ? resolveImageUrl(item.foto) : undefined,
  });
  useJsonLd('sale-detail-jsonld', item ? {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: item.titulo,
    vehicleModelDate: item.ano,
    mileageFromOdometer: { '@type': 'QuantitativeValue', value: item.quilometros, unitCode: 'KMT' },
    fuelType: FUEL[item.combustivel] || item.combustivel,
    offers: { '@type': 'Offer', price: item.preco, priceCurrency: 'EUR',
              availability: item.contactavel ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut' },
  } : {});

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 p-8" data-testid="sale-detail-error">
        <p className="text-lg font-bold text-slate-900">{err}</p>
        <Link to="/comprar-viaturas-tvde" className="mt-4 inline-block rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white">
          Ver viaturas disponíveis
        </Link>
      </div>
    );
  }
  if (!item) return <div className="p-10 text-center text-slate-500" data-testid="sale-detail-loading">A carregar…</div>;

  const v = item.viatura || {};
  const c = item.comercial || {};
  const e = item.estado || {};
  const h = item.historico_tvde || {};
  const fotos = item.fotos || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sale-detail-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-4 py-4 md:px-8">
          <button onClick={() => navigate('/comprar-viaturas-tvde')} className="inline-flex items-center gap-2 text-sm font-bold text-slate-900" data-testid="sale-detail-back">
            <ArrowLeft className="h-4 w-4" /> Comprar viaturas
          </button>
          <Link to="/vender-viatura" className="rounded-xl bg-slate-900 px-3.5 py-2 text-xs font-bold text-white" data-testid="sale-detail-sell-cta">
            Vender uma viatura
          </Link>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-[1200px] gap-6 px-4 py-8 md:px-8 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="aspect-[16/10] bg-slate-100">
              {fotos.length ? (
                <img src={resolveImageUrl(fotos[photo])} alt={item.titulo} className="h-full w-full object-cover" data-testid="sale-detail-photo" />
              ) : (
                <div className="flex h-full items-center justify-center text-slate-300"><Car className="h-12 w-12" /></div>
              )}
            </div>
            {fotos.length > 1 && (
              <div className="flex gap-2 overflow-x-auto p-3" data-testid="sale-detail-gallery">
                {fotos.map((f, i) => (
                  <button key={i} onClick={() => setPhoto(i)} className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 ${i === photo ? 'border-emerald-500' : 'border-transparent'}`} data-testid={`sale-detail-thumb-${i}`}>
                    <img src={resolveImageUrl(f)} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mt-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-bold uppercase text-white" data-testid="sale-detail-category">{item.categoria_label}</span>
              <PromoBadges promocoes={item.promocoes} testIdPrefix="sale-detail" />
              {!item.contactavel && (
                <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[10px] font-bold uppercase text-rose-700 ring-1 ring-rose-200" data-testid="sale-detail-unavailable">
                  Já não disponível
                </span>
              )}
            </div>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="sale-detail-title">{item.titulo}</h1>
            <p className="mt-2 text-3xl font-extrabold text-slate-900" data-testid="sale-detail-price">
              {money(item.preco)} {c.preco_negociavel && <span className="text-sm font-semibold text-slate-500">negociável</span>}
            </p>
            <p className="mt-1 inline-flex items-center gap-1 text-sm text-slate-500">
              <MapPin className="h-4 w-4" /> {item.localizacao} · {VENDEDOR[item.tipo_vendedor] || '—'} · {item.nome_publico}
            </p>
          </div>

          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sale-detail-specs">
            <h2 className="text-base font-bold text-slate-900 md:text-lg">Especificações declaradas</h2>
            <div className="mt-3 grid gap-x-8 md:grid-cols-2">
              <Row label="Marca" value={v.marca} testId="spec-marca" />
              <Row label="Modelo" value={v.modelo} testId="spec-modelo" />
              <Row label="Versão" value={v.versao} />
              <Row label="Ano" value={v.ano} testId="spec-ano" />
              <Row label="Mês da 1.ª matrícula" value={v.mes_matricula} />
              <Row label="Quilómetros" value={km(v.quilometros)} testId="spec-km" />
              <Row label="Origem" value={v.origem === 'importado' ? `Importado${v.pais_origem ? ` (${v.pais_origem})` : ''}` : 'Nacional'} />
              <Row label="Combustível" value={FUEL[v.combustivel] || v.combustivel} />
              <Row label="Caixa" value={v.caixa === 'automatica' ? 'Automática' : 'Manual'} />
              <Row label="Potência" value={v.potencia ? `${v.potencia} cv` : ''} />
              <Row label="Cilindrada" value={v.cilindrada ? `${v.cilindrada} cm³` : ''} />
              <Row label="Tração" value={v.tracao} />
              <Row label="Lugares" value={v.lugares} />
              <Row label="Portas" value={v.portas} />
              <Row label="Cor" value={v.cor} />
            </div>
          </section>

          <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sale-detail-condition">
            <h2 className="text-base font-bold text-slate-900 md:text-lg">Estado da viatura (declarado)</h2>
            <div className="mt-3 grid gap-x-8 md:grid-cols-2">
              <Row label="Estado principal" value={ESTADO[e.principal] || e.principal} testId="estado-principal" />
              <Row label="Estado geral" value={e.geral} />
              <Row label="Mecânica" value={e.mecanico} />
              <Row label="Pintura e chapa" value={e.pintura} />
              <Row label="Interior" value={e.interior} />
              <Row label="Pneus" value={e.pneus} />
              <Row label="Avarias conhecidas" value={e.avarias} />
              <Row label="Acidentes ou danos" value={e.acidentes} />
              <Row label="Manutenção" value={e.manutencao} />
              <Row label="Livro de revisões" value={TRI[e.livro_revisoes]} />
              <Row label="Chaves" value={e.chaves} />
              <Row label="Inspeção válida" value={TRI[e.inspecao_valida]} />
              <Row label="Próxima inspeção" value={e.proxima_inspecao} />
              <Row label="Estado da bateria" value={e.bateria_percentagem ? `${e.bateria_percentagem}%` : e.bateria_estado} />
              <Row label="Autonomia declarada" value={e.autonomia ? `${e.autonomia} km` : ''} />
              <Row label="Cabo de carregamento" value={e.cabo_carregamento === true ? 'Incluído' : e.cabo_carregamento === false ? 'Não incluído' : ''} />
              <Row label="Tipo de carregamento" value={e.tipo_carregamento} />
            </div>
            <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-500">
              Estes dados são declarados pelo anunciante e não são verificados pelo Marketplace.
            </p>
          </section>

          <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sale-detail-tvde">
            <h2 className="text-base font-bold text-slate-900 md:text-lg">Histórico TVDE declarado</h2>
            <div className="mt-3 grid gap-x-8 md:grid-cols-2">
              <Row label="Já trabalhou em TVDE" value={h.trabalhou === true ? 'Sim' : h.trabalhou === false ? 'Não' : ''} />
              <Row label="Início da atividade" value={h.ano_inicio} />
              <Row label="Fim da atividade" value={h.ano_fim} />
              <Row label="Tempo em atividade" value={h.meses_atividade ? `${h.meses_atividade} meses` : ''} />
              <Row label="Plataformas indicadas" value={(h.plataformas || []).join(', ')} />
              <Row label="Categorias indicadas" value={(h.categorias || []).join(', ')} />
              <Row label="Motivo de saída" value={h.motivo_saida} />
              <Row label="Porque é oportunidade" value={h.porque_oportunidade} />
            </div>
            <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="sale-detail-disclaimer">
              {item.disclaimer}
            </p>
          </section>

          <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sale-detail-commercial">
            <h2 className="text-base font-bold text-slate-900 md:text-lg">Condições comerciais declaradas</h2>
            <div className="mt-3 grid gap-x-8 md:grid-cols-2">
              <Row label="Preço" value={money(c.preco)} />
              <Row label="Negociação" value={c.preco_negociavel ? 'Preço negociável' : 'Preço fixo'} />
              <Row label="Financiamento" value={c.financiamento ? 'Declarado pelo anunciante' : 'Não declarado'} />
              <Row label="Retoma" value={c.retoma ? 'Aceita retoma (declarado)' : 'Não declarado'} />
              <Row label="Garantia" value={c.garantia ? `Declarada${c.garantia_meses ? ` · ${c.garantia_meses} meses` : ''}` : 'Não declarada'} />
              <Row label="Venda urgente" value={c.venda_urgente ? 'Sim' : ''} />
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Financiamento, garantia e retoma são declarações do anunciante — o Marketplace não presta nem garante estes serviços.
            </p>
          </section>

          <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sale-detail-description">
            <h2 className="text-base font-bold text-slate-900 md:text-lg">Descrição</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{item.descricao}</p>
            {item.equipamentos && (
              <>
                <h3 className="mt-4 text-sm font-bold text-slate-900">Equipamentos e extras</h3>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{item.equipamentos}</p>
              </>
            )}
            {item.defeitos && (
              <>
                <h3 className="mt-4 text-sm font-bold text-slate-900">Aspetos a conhecer</h3>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{item.defeitos}</p>
              </>
            )}
            <p className="mt-4 text-xs text-slate-400">Publicado a {(item.publicado_em || '').slice(0, 10) || '—'}</p>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="sale-detail-contact-box">
            <p className="text-sm font-bold text-slate-900">Contactar o anunciante</p>
            <p className="mt-1 text-xs text-slate-500">
              {item.contactavel
                ? 'O contacto é enviado através do Marketplace, com verificação de email.'
                : 'Este anúncio já não aceita novos contactos.'}
            </p>
            {item.contactavel && item.contacto_publico?.telefone && (
              <p className="mt-2 text-sm font-bold text-emerald-700" data-testid="sale-detail-phone">
                {item.contacto_publico.telefone}
              </p>
            )}
            {item.contactavel ? (
              <>
                <button
                  onClick={() => setShowContact((s) => !s)}
                  className="mt-3 w-full rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-600"
                  data-testid="sale-detail-contact-btn"
                >
                  {showContact ? 'Fechar' : 'Enviar mensagem'}
                </button>
                {showContact && (
                  <div className="mt-3">
                    {conversasAtivas
                      ? <ConversationStartForm listingId={item.id} titulo={item.titulo} />
                      : <ListingContactForm listingId={item.id} />}
                  </div>
                )}
              </>
            ) : (
              <Link to="/comprar-viaturas-tvde" className="mt-3 block rounded-xl bg-slate-900 px-4 py-2.5 text-center text-sm font-bold text-white" data-testid="sale-detail-see-others">
                Ver outras viaturas
              </Link>
            )}
            <button
              onClick={() => setShowReport(true)}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600"
              data-testid="sale-detail-report-btn"
            >
              <Flag className="h-3.5 w-3.5" /> Denunciar anúncio
            </button>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900" data-testid="sale-detail-safety">
            <p className="inline-flex items-center gap-2 font-bold"><AlertTriangle className="h-4 w-4" /> Compre com segurança</p>
            <ul className="mt-2 list-disc space-y-1 pl-4">
              <li>Veja a viatura pessoalmente e em local público.</li>
              <li>Nunca faça transferências ou sinais antecipados.</li>
              <li>Confirme o estado real e a documentação diretamente com o vendedor.</li>
              <li>Confirme junto das plataformas TVDE qualquer requisito de elegibilidade.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-500" data-testid="sale-detail-privacy">
            <p className="inline-flex items-center gap-2 font-bold text-slate-800"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Privacidade</p>
            <p className="mt-1">
              Mostramos apenas o nome público do anunciante. Os dados reais verificados permanecem privados e são
              utilizados para segurança, contacto e moderação.
            </p>
          </div>

          {(item.semelhantes || []).length > 0 && (
            <div data-testid="sale-detail-similar">
              <p className="mb-2 text-sm font-bold text-slate-900">Viaturas semelhantes</p>
              <div className="grid gap-3">
                {item.semelhantes.map((s) => <SaleCard key={s.id} item={s} />)}
              </div>
            </div>
          )}
          <AdSlot posicao="sales_detail_sidebar" />
        </aside>
      </main>

      {showReport && <ReportListingModal listingId={item.id} onClose={() => setShowReport(false)} />}
      <MarketplaceFooter />
    </div>
  );
}
